import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Check, X, Clock, Heart, Users, Baby, Home, PawPrint, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useMatching } from '@/hooks/useMatching';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslation } from 'react-i18next';

interface MatchRequestCardProps {
  request: any;
  type: 'received' | 'sent';
  onOpenChat?: (match: any) => void;
}

const MATCH_TYPE_CONFIG = {
  playdate: { icon: Heart, key: 'playdate', color: 'text-pink-500' },
  friendship: { icon: Users, key: 'friendship', color: 'text-blue-500' },
  breeding: { icon: Baby, key: 'breeding', color: 'text-purple-500' },
  adoption: { icon: Home, key: 'adoption', color: 'text-green-500' },
};

export const MatchRequestCard = ({ request, type, onOpenChat }: MatchRequestCardProps) => {
  const { t } = useTranslation();
  const { acceptMatchRequest, rejectMatchRequest, deleteMatchRequest } = useMatching();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isReceived = type === 'received';
  const isPending = request.status === 'pending';
  const displayPet = isReceived ? request.requester_pet : request.target_pet;
  
  const matchConfig = MATCH_TYPE_CONFIG[request.match_type as keyof typeof MATCH_TYPE_CONFIG];
  const MatchIcon = matchConfig?.icon || Heart;

  const getStatusBadge = () => {
    switch (request.status) {
      case 'pending':
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />{t('matchInbox.status.pending')}</Badge>;
      case 'accepted':
        return <Badge className="bg-green-500 gap-1"><Check className="h-3 w-3" />{t('matchInbox.status.accepted')}</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="gap-1"><X className="h-3 w-3" />{t('matchInbox.status.rejected')}</Badge>;
      case 'expired':
        return <Badge variant="outline" className="gap-1">{t('matchInbox.status.expired')}</Badge>;
      default:
        return null;
    }
  };

  const handleAccept = async () => {
    const result = await acceptMatchRequest.mutateAsync(request.id);
    
    // Auto-redirect to chat if available
    if (result?.match && result?.chat_room_id && onOpenChat) {
      onOpenChat({
        id: result.match.id,
        chat_room: { id: result.chat_room_id },
        pet_1: request.requester_pet,
        pet_2: request.target_pet,
        user_1_id: request.requester_user_id,
        user_2_id: request.target_user_id
      });
    }
  };

  const handleReject = async () => {
    await rejectMatchRequest.mutateAsync(request.id);
    setShowRejectDialog(false);
  };

  const handleDelete = async () => {
    await deleteMatchRequest.mutateAsync(request.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start gap-4">
          {/* Pet Avatar */}
          <Avatar className="h-16 w-16 flex-shrink-0">
            {displayPet?.photo_url ? (
              <AvatarImage src={displayPet.photo_url} alt={displayPet.name} />
            ) : (
              <AvatarFallback className="bg-pink-100">
                <PawPrint className="h-8 w-8 text-pink-500" />
              </AvatarFallback>
            )}
          </Avatar>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg">{displayPet?.name}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                  <MatchIcon className={`h-4 w-4 ${matchConfig?.color}`} />
                  <span>{t(`requestMatch.matchTypes.${matchConfig?.key}`)}</span>
                  {request.compatibility_score && (
                    <>
                      <span>•</span>
                      <span className="text-pink-600 font-medium">
                        {request.compatibility_score}% match
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {getStatusBadge()}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Message */}
            {request.message && (
              <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                "{request.message}"
              </p>
            )}

            {/* Meta info */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
              <span>{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</span>
              {request.expires_at && isPending && (
                <>
                  <span>•</span>
                  <span>{t('requestMatch.expires')} {formatDistanceToNow(new Date(request.expires_at), { addSuffix: true })}</span>
                </>
              )}
              <span>•</span>
              <span>{request.credits_used} {request.credits_used > 1 ? t('requestMatch.credits') : t('requestMatch.credit')}</span>
            </div>

            {/* Actions for received pending requests */}
            {isReceived && isPending && (
              <div className="flex gap-2">
                <Button
                  onClick={handleAccept}
                  disabled={acceptMatchRequest.isPending}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                >
                  <Check className="h-4 w-4 mr-2" />
                  {t('matchInbox.actions.accept')}
                </Button>
                <Button
                  onClick={() => setShowRejectDialog(true)}
                  disabled={rejectMatchRequest.isPending}
                  variant="outline"
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('matchInbox.actions.decline')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Reject Confirmation Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('matchInbox.actions.decline')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('matchInbox.actions.decline')} {displayPet?.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('matchModal.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('matchInbox.actions.decline')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('matchInbox.actions.delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('matchInbox.actions.deleteRequestDesc', { petName: displayPet?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('matchModal.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('matchInbox.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
